"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import { Mark } from "@/components/services/shared/demo-parts";

import { useShowcaseShellHeight } from "@/components/services/shared/useShowcaseShellHeight";

import { CcAdPreview } from "./CcAdPreview";
import type { AdvertisingDemoCopy } from "./advertising-demo-copy";

type CcAdShowcaseProps = {
  copy: AdvertisingDemoCopy["showcase"];
};

const AD_SHOWCASE_ENTER_MS = 560;

/**
 * Before/after of ads in four channels: a typical ad next to one written to
 * sell, with what we changed. Same layout and motion as the lead generation
 * and AI automation showcases (intro, sticky pills, two panels side by side).
 */
export function CcAdShowcase({ copy }: CcAdShowcaseProps) {
  const [activeId, setActiveId] = useState(copy.cases[0]?.id ?? "");
  const [entering, setEntering] = useState(false);
  const prefersReducedMotion = useCcPrefersReducedMotion();
  const introShellRef = useRef<HTMLDivElement>(null);
  const introInnerRef = useRef<HTMLDivElement>(null);
  const flowsShellRef = useRef<HTMLDivElement>(null);
  const flowsInnerRef = useRef<HTMLDivElement>(null);
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const active = copy.cases.find((item) => item.id === activeId) ?? copy.cases[0];

  const selectCase = useCallback(
    (id: string) => {
      if (id === activeId) return;
      setActiveId(id);
      if (prefersReducedMotion) return;
      setEntering(true);
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = setTimeout(() => setEntering(false), AD_SHOWCASE_ENTER_MS);
    },
    [activeId, prefersReducedMotion],
  );

  useShowcaseShellHeight(
    introShellRef,
    introInnerRef,
    prefersReducedMotion,
    activeId,
    active?.title,
    active?.text,
    active?.points,
  );

  useShowcaseShellHeight(
    flowsShellRef,
    flowsInnerRef,
    prefersReducedMotion,
    activeId,
    active?.before,
    active?.after,
  );

  useEffect(
    () => () => {
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
    },
    [],
  );

  if (!active) return null;

  const copyBlocks = (
    <div className="cc-web-showcase__copies">
      {copy.cases.map((item) => {
        const isActive = item.id === active.id;
        return (
          <div
            key={item.id}
            className="cc-web-showcase__copy"
            data-active={isActive}
            aria-hidden={!isActive}
            inert={!isActive}
          >
            <CcSanjayaEyebrow theme="on-subtle">{item.name}</CcSanjayaEyebrow>
            <h3 className="cc-web-showcase__title">{item.title}</h3>
            <p className="cc-web-showcase__text">{item.text}</p>
            <ul className="cc-web-showcase__points">
              {item.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );

  const casePills = (
    <div className="cc-auto-showcase__case-switch-shell">
      <div
        className="cc-web-showcase__switch cc-auto-showcase__case-switch"
        role="group"
        aria-label={copy.tablistLabel}
      >
        {copy.cases.map((item) => (
          <button
            key={item.id}
            type="button"
            className="cc-web-showcase__pill"
            aria-pressed={item.id === active.id}
            onClick={() => selectCase(item.id)}
          >
            <Mark logo={item.logo} badge={item.badge} />
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <section
      className="cc-section cc-web-showcase-section cc-ai-showcase-section cc-auto-showcase-section cc-lead-showcase-section cc-ad-showcase-section"
      aria-labelledby="cc-ad-showcase-title"
      data-cc-nav="light"
      {...devSectionAttr("ad-showcase")}
    >
      <div className="cc-container">
        <h2 id="cc-ad-showcase-title" className="cc-web-showcase__sr">
          {copy.sectionTitle}
        </h2>
        <div className="cc-web-showcase cc-auto-showcase">
          <div className="cc-auto-showcase__intro-row">
            <div className="cc-auto-showcase__intro-copy-shell" ref={introShellRef}>
              <div
                ref={introInnerRef}
                className="cc-auto-showcase__intro-copy"
                data-entering={entering ? "true" : undefined}
              >
                {copyBlocks}
              </div>
            </div>
            {casePills}
          </div>

          <figure
            id="cc-ad-showcase-ads"
            className="cc-web-showcase__media cc-auto-showcase__demo"
          >
            <div className="cc-auto-showcase__flows-shell" ref={flowsShellRef}>
              <div
                className="cc-auto-showcase__flows"
                ref={flowsInnerRef}
                data-entering={entering ? "true" : undefined}
              >
                {(["before", "after"] as const).map((state) => (
                  <div key={state} className="cc-auto-showcase__flow-panel">
                    <CcAdPreview
                      key={`${active.id}-${state}`}
                      channel={active.channel}
                      logo={active.logo}
                      badge={active.badge}
                      stateLabel={state === "before" ? copy.beforeLabel : copy.afterLabel}
                      stateVariant={state}
                      advertiser={active.advertiser}
                      sponsoredLabel={copy.sponsoredLabel}
                      query={active.query}
                      ad={active[state]}
                      chipsLabel={state === "before" ? copy.beforeChipsLabel : copy.afterChipsLabel}
                    />
                  </div>
                ))}
              </div>
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}
